//@flow
import * as React from 'react';
import classnames from 'classnames';
import { themr } from 'react-css-themr';
import { compose } from 'recompose';
import { translate, useTranslation } from 'react-i18next';
import { components } from 'react-select';

import ContextMenu from 'common/components/core/context_menu/ContextMenu';
import HandleOutsideClick from 'common/components/core/util/HandleOutsideClick';
import { KeyEventHandler } from 'common/components/core/util/KeyEventsHandler';
import theme from './TeamSelector.scss';
import { isEqual, includes, keys, isArray, debounce, min } from 'lodash';
import { memoized } from 'common/utils/Memoizer';
import TeamChip from 'common/components/core/common/TeamChip';
import {
    MultiSelectFilter,
    SelectTypes,
    selectWithButtonFactory
} from 'common/components/core/select';
import ArrayUtil from 'common/utils/ArrayUtil';
import { TeamEnum, TeamStatusEnum } from 'common/enums/TeamEnum';
import {
    Option,
    NoOptionsMessage,
    ValueContainer,
    MenuList,
    SelectTriggerElement
} from 'common/components/core/select/MultiSelectFilter';
import Switch from 'common/components/core/switch';
import { Box, Flex, Text, Chip, Position } from '@locus-taxy/neo-ui';
import coreIds from 'common/tests/core/coreIds.json';
import commonIds from 'common/tests/common/common.json';
import { SizeEnum } from 'common/enums/UIEnums';
import useShouldIncludeInactiveTeams from 'common/hooks/useShouldIncludeInactiveTeams';
import TeamsRow from 'common/components/common/TeamsRow';

const Menu = props => {
    const { t } = useTranslation();
    const { selectProps, children } = props;

    const {
        theme,
        menuAutoWidth,
        selectSize,
        'data-test-id': dataTestId = coreIds.select,
        shouldIncludeInactiveTeams,
        setShouldIncludeInactiveTeams
    } = selectProps;
    const innerProps = {
        ...props.innerProps,
        'data-test-id': `${dataTestId}-${coreIds.selectMenu}`
    };
    const selectSizeClass = SizeEnum.getSizeClass({
        theme,
        size: selectSize,
        suffix: 'SelectMenu'
    });

    return (
        <components.Menu
            {...props}
            innerProps={innerProps}
            className={classnames(theme.selectMenu, {
                [theme.menuAutoWidth]: menuAutoWidth,
                [selectSizeClass]: !!selectSizeClass
            })}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                padding={3}
                backgroundColor="grey.100"
                height="40px"
            >
                <Text color="grey.900">{t('analytics:showInactiveTeams')}</Text>
                <Box padding={1}>
                    <Switch
                        testId={`${dataTestId}-${coreIds.inactiveTeamsToggle}`}
                        checked={shouldIncludeInactiveTeams}
                        onChange={setShouldIncludeInactiveTeams}
                    />
                </Box>
            </Flex>
            {children}
        </components.Menu>
    );
};

const SelectComponent = selectWithButtonFactory({
    Option,
    NoOptionsMessage,
    ValueContainer,
    showValueOnOpen: true,
    MenuList,
    SelectTriggerElement,
    Menu
});

const EnhancedContextMenu = HandleOutsideClick(KeyEventHandler(ContextMenu));

type OwnProps = {
    value: Array<string>,
    teams: ?Array<Object>,
    onChange: Function,
    shouldShowInactiveTeamSelector?: ?boolean,
    fixedVisibleTeamsCount?: number
};

type Props = {
    ...OwnProps,
    theme: Object,
    shouldIncludeInactiveTeams: boolean,
    setShouldIncludeInactiveTeams: boolean => void
};

type State = {
    contextMenuTeamId: ?string,
    forceShowSelect: boolean,
    selectedTeamIds: Array,
    availableTeamsRowWidth: number
};

class TeamSelector extends React.PureComponent<Props, State> {
    teamIdToTeamObjectMap: Map;
    contextMenuPortalRef: React.RefObject;

    static defaultProps = {
        showOverlay: true,
        fixedVisibleTeamsCount: 20
    };

    constructor(props) {
        super(props);

        this.state = {
            contextMenuTeamId: null,
            selectedTeamIds: [],
            forceShowSelect: false,
            availableTeamsRowWidth: 80
        };

        this.teamIdToTeamObjectMap = this.getTeamIdToTeamMap(props.teams);
    }

    componentDidMount() {
        window.addEventListener('resize', this.debouncedHandleResize);
        this.debouncedHandleResize();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { teams: oldTeams } = this.props;
        const { teams, value } = nextProps;

        if (!isEqual(teams, oldTeams)) {
            this.teamIdToTeamObjectMap = this.getTeamIdToTeamMap(teams);
        }

        if (includes(value, TeamEnum.NoTeam)) {
            this.onShowSelect();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { value } = this.props;
        const { forceShowSelect } = prevState;

        if (forceShowSelect && !includes(value, TeamEnum.NoTeam)) {
            this.setState({
                forceShowSelect: false
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.debouncedHandleResize);
    }

    teamSelectorContainerRef = React.createRef<HTMLDivElement>();

    debouncedHandleResize = debounce(() => {
        if (this.teamSelectorContainerRef.current) {
            const containerWidth = this.teamSelectorContainerRef.current
                .clientWidth;
            this.setState({
                availableTeamsRowWidth: containerWidth - 150
            });
        }
    });

    @memoized()
    getTeamIdToTeamMap(teams) {
        const teamsMap = {};
        if (teams?.length) {
            teams.forEach(team => {
                teamsMap[team.teamId] = team;
            });
        }

        return teamsMap;
    }

    @memoized()
    getSource(teams) {
        const teamsMap = {};
        if (teams?.length) {
            teams.forEach(team => {
                teamsMap[team.teamId] = team.name ? team.name : team.teamId;
            });
        }

        return teamsMap;
    }

    @memoized()
    getValue(value, teams, selectedTeamIds) {
        if (selectedTeamIds) {
            return selectedTeamIds;
        }

        if (!value || value.length === 0) {
            return keys(this.getSource(teams));
        }

        return value.filter(value => value !== TeamEnum.NoTeam);
    }

    onShowSelect = () => this.setState({ forceShowSelect: true });

    getOptionValue = option => option.teamId;

    getOptionLabel = option => option.name;

    // Need to add formatOptionLabel here because using getOptionLabel for presentational logic is problematic.
    formatOptionLabel = option => {
        const { t } = this.props;
        const isTeamInactive = option.status === TeamStatusEnum.Inactive;

        if (!isTeamInactive) return option.name;

        return (
            <Flex justifyContent="space-between" alignItems="center">
                <Box color="grey.600">{option.name}</Box>
                <Chip>{t('analytics:dashboardEditor.inactive')}</Chip>
            </Flex>
        );
    };

    handleChange = ({ values }) => {
        const { onChange, value, teams } = this.props;
        const idList = values.map(value => this.getOptionValue(value));
        const selectedTeams = this.getValue(value, teams, values);
        // on clear selection

        if (!selectedTeams.length) return;

        if (!idList.length) {
            onChange(null);
            return;
        }

        onChange(idList);
    };

    showContextMenu = (event, id) => {
        event.preventDefault();

        this.setState({
            x: event.clientX,
            y: event.clientY,
            contextMenuTeamId: id,
            target: event.target
        });
    };

    closeContextMenu = () => {
        this.setState({
            x: null,
            y: null,
            contextMenuTeamId: null,
            target: null
        });
    };

    renderAllSelectedTeams = () => {
        const { theme, value, teams } = this.props;
        const { contextMenuTeamId } = this.state;
        var selectedTeams = this.getValue(value, teams);

        if (!selectedTeams.length) return [];

        return selectedTeams.map(key => {
            const team = this.teamIdToTeamObjectMap[key];
            if (!team) return null;

            let isContextMenuOpenForChip = false;
            if (contextMenuTeamId) {
                isContextMenuOpenForChip = contextMenuTeamId === team.teamId;
            }

            return (
                <TeamChip
                    theme={theme}
                    team={team}
                    key={team.teamId}
                    onContextMenu={this.showContextMenu}
                    onClick={this.showContextMenu}
                    id={team.teamId}
                    className={classnames({
                        [theme.activeChip]: isContextMenuOpenForChip
                    })}
                />
            );
        });
    };

    renderTeamsRow = () => {
        const { value, teams, fixedVisibleTeamsCount } = this.props;
        const { availableTeamsRowWidth } = this.state;
        const selectedTeams = this.getValue(value, teams);
        if (selectedTeams.length === 0) return [];

        const _selectedTeams = teams.filter(team => {
            return selectedTeams.includes(team.teamId);
        });

        return (
            <Position
                data-test-id={commonIds.teamSelector.selectedTeams}
                width="calc(100% - 180px)"
                position="relative"
                left={160}
                height={54}
                display="flex"
                alignItems="center"
            >
                <TeamsRow
                    teams={_selectedTeams}
                    visibleCount={min([
                        Math.floor(availableTeamsRowWidth / 100),
                        fixedVisibleTeamsCount
                    ])}
                    onContextMenu={this.showContextMenu}
                    onClick={this.showContextMenu}
                    overflowPlacement="bottom"
                    overflowButtonPalette="light"
                />
            </Position>
        );
    };

    handleRemoveTeam = e => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        const { value, teams, onChange } = this.props;
        const { contextMenuTeamId } = this.state;

        const selectedTeams = this.getValue(value, teams).filter(
            teamId => teamId !== contextMenuTeamId
        );

        onChange(selectedTeams);

        this.closeContextMenu();
    };

    handleRemoveOtherTeams = e => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        const { value, teams } = this.props;
        const { contextMenuTeamId } = this.state;

        const selectedTeams = this.getValue(value, teams).filter(
            teamId => teamId === contextMenuTeamId
        );

        this.props.onChange(selectedTeams);

        this.closeContextMenu();
    };

    getContextMenuPortalRef = ref => {
        this.contextMenuPortalRef = ref;
    };

    getContextMenuContainer = () => {
        return this.contextMenuPortalRef;
    };

    renderContextMenu = () => {
        const { t } = this.props;
        const { x, y } = this.state;

        if (!x || !y) return null;

        const items = [
            {
                value: t('removeTeams'),
                onClick: this.handleRemoveTeam,
                testId: 'removeTeam'
            },
            {
                value: t('removeOtherTeams'),
                onClick: this.handleRemoveOtherTeams,
                testId: 'removeOtherTeams'
            }
        ];

        return (
            <EnhancedContextMenu
                className={theme.contextMenuWrap}
                onEscape={this.closeContextMenu}
                getContextMenuPortalRef={this.getContextMenuPortalRef}
                x={this.state.x}
                y={this.state.y}
                target={this.state.target}
                handleClickOutside={this.closeContextMenu}
                items={items}
                getContainer={this.getContextMenuContainer}
            />
        );
    };

    @memoized()
    sortTeamsByName(teams) {
        if (isArray(teams)) {
            return ArrayUtil.sortByKey(teams, 'name');
        }
        return [];
    }

    render() {
        const {
            theme,
            value,
            teams,
            t,
            showOverlay,
            shouldShowInactiveTeamSelector,
            ...rest
        } = this.props;
        const { forceShowSelect } = this.state;
        const selectedTeams = this.getValue(value, teams);
        const teamOptions = this.sortTeamsByName(teams);

        return (
            <div
                className={theme.teamSelector}
                data-test-id={coreIds.teamsSelector}
                ref={this.teamSelectorContainerRef}
            >
                <MultiSelectFilter
                    {...rest}
                    showOverlay={showOverlay}
                    theme={theme}
                    className={theme.teamSelectFilter}
                    forceShowSelect={forceShowSelect}
                    disableSubmitOnEmpty
                    submitOnHide
                    shakeOnError
                    showSelectFooter={false}
                    clearable={false}
                    disableBlanksSelectOption
                    maxMenuHeight={250}
                    icon={'caret_down'}
                    placeholder={t('searchTeams')}
                    iconClassName={theme.icon}
                    selectType={SelectTypes.Outline}
                    options={teamOptions}
                    value={selectedTeams}
                    getOptionValue={this.getOptionValue}
                    getOptionLabel={this.getOptionLabel}
                    formatOptionLabel={this.formatOptionLabel}
                    onChange={this.handleChange}
                    buttonTextTanslationKey={'countOfTeamsSelected'} //TODO: Find better way to handle this
                    selectComponent={
                        shouldShowInactiveTeamSelector
                            ? SelectComponent
                            : undefined
                    }
                />
                {this.renderTeamsRow()}
                {this.renderContextMenu()}
            </div>
        );
    }
}

const withShouldIncludeInactiveTeams = React.forwardRef((props, ref) => {
    const [
        shouldIncludeInactiveTeams,
        setShouldIncludeInactiveTeams
    ] = useShouldIncludeInactiveTeams();
    return (
        <TeamSelector
            {...props}
            shouldIncludeInactiveTeams={shouldIncludeInactiveTeams}
            setShouldIncludeInactiveTeams={setShouldIncludeInactiveTeams}
            ref={ref}
        />
    );
});

withShouldIncludeInactiveTeams.displayName = `withShouldIncludeInactiveTeams(${
    TeamSelector.displayName || TeamSelector.name
})`;

export const TeamSelectorForStorybook = compose(
    themr('TeamSelector', theme),
    translate('translations')
)(TeamSelector);

export default (compose(
    themr('TeamSelector', theme),
    translate('translations')
)(withShouldIncludeInactiveTeams): React.AbstractComponent<OwnProps>);
